import React, { useMemo } from "react";
import { Button, Modal, Row, Typography, Form, FormInstance, Col, Input, Select, Space } from "antd";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { generateErrorMessage } from "src/utils/error-message";
import { SentOneStackOfFlareEnum, SentOneStackOfFlareOption, VolumeByEnum, VolumeByOption } from "src/entity/flare/flare.constants";
import { CalculationMethodFixed, InputByFixed, InputByFixedEnum } from "src/entity/common/unit.constant";
import { useShallow } from "zustand/react/shallow";
import { ManageCompoundProps } from "src/entity/flare/heating-value.entity";
import { PlusCircleOutlined } from "@ant-design/icons";
import { isNil } from "lodash";
import { utilsStore } from "src/stores/utils-store";
import { flareVolumeStore } from "src/stores/flare-volume-store";
import CustomFormDatePicker from "src/components/AntdCustomPart/CustomFormDatePicker/CustomFormDatePicker";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import GroupFlareTagPi from "src/components/AntdCustomPart/GroupFlareTagPi/GroupFlareTagPi";
import { InputByEnum } from "src/entity/fuel-volume/fuel-volume.entity";
import { formatNumber } from "src/utils/process-number";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";

const { Text } = Typography;

interface FlareVolumeModalProps {
  submitSetupHeatingValue: () => void;
  loadingManageFlareVolume: boolean;
  visibleModal: boolean;
  closeModal: () => void;
  errorFlareVolume: string | null;
  formSetupFlareVolume: FormInstance;
  calculateHeatingValue: (data: ManageCompoundProps[]) => void;
  isEdit: boolean;
}

const FlareVolumeModal: React.FC<FlareVolumeModalProps> = (props) => {
  const dataFlareInfo = flareVolumeStore(useShallow((state) => state.dataFlareInfo));
  const dataAreaIncompany = utilsStore(useShallow((state) => state.dataAreaInCompany));
  const dataHeatingValueAll = flareVolumeStore(useShallow((state) => state.dataHeatingValueAll));
  const clearError = flareVolumeStore(useShallow((state) => state.clearError));
  const { submitSetupHeatingValue, loadingManageFlareVolume, closeModal, visibleModal, errorFlareVolume, formSetupFlareVolume, isEdit } =
    props;

  const optionsAreaInCompany = useMemo(() => {
    return dataAreaIncompany.map((item) => ({
      label: item.areaName,
      value: item._id,
    }));
  }, [dataAreaIncompany]);
  const optionsFlareInformation = useMemo(() => {
    return dataFlareInfo.map((item) => ({
      label: item.flareName,
      value: item._id,
    }));
  }, [dataFlareInfo]);
  const optionsHeatingValues = useMemo(() => {
    return dataHeatingValueAll.map((item) => ({
      label: item.gasName,
      value: item._id,
    }));
  }, [dataHeatingValueAll]);

  const inputBy = Form.useWatch("inputBy", formSetupFlareVolume);
  const volumeBy = Form.useWatch("volumeBy", formSetupFlareVolume);
  const sentOneStackOfFlare = Form.useWatch("sentOneStackOfFlare", formSetupFlareVolume);
  const turnaroundPeriodRanges = Form.useWatch("turnaroundPeriod", formSetupFlareVolume);
  const emergencyPeriodRanges = Form.useWatch("emergencyPeriod", formSetupFlareVolume);
  const exceptionDateRanges = Form.useWatch("exceptionDate", formSetupFlareVolume);
  // console.log("formSetupFlareVolume : ", formSetupFlareVolume.getFieldsValue());

  // console.log("Data Date Ranges : ", {
  //   turnaroundPeriodRanges,
  //   emergencyPeriodRanges,
  //   exceptionDateRanges,
  // });

  const onChangeSentOneStackOfFlare = (val: string) => {
    // console.log("sentOneStackOfFlare :: ", val);
    // if (val === "No") {
    //   setTimeout(() => {
    //     const elementBtn: HTMLElement | null = document.querySelector("#add-tag-pi-portion-group");
    //     if (elementBtn) {
    //       elementBtn.click();
    //     }
    //   }, 200);
    // }
    clearErrorFlareVolume();
  };

  const clearErrorFlareVolume = () => {
    if (errorFlareVolume) clearError("errorFlareVolume");
  };

  const onChangeFlareVolume = (val: string) => {
    if (val === InputByEnum.MANUAL) {
      formSetupFlareVolume.setFieldValue("hcVolume", undefined);
      formSetupFlareVolume.setFieldValue("volumeBy", undefined);
      formSetupFlareVolume.setFieldValue("maxFlow", undefined);
      formSetupFlareVolume.setFieldValue("percentPortion", undefined);
      formSetupFlareVolume.setFieldValue("tagPi", undefined);
      formSetupFlareVolume.setFieldValue("sentOneStackOfFlare", undefined);
      formSetupFlareVolume.setFieldValue("turnaroundPeriod", []);
      formSetupFlareVolume.setFieldValue("emergencyPeriod", []);
      formSetupFlareVolume.setFieldValue("exceptionDate", []);
      formSetupFlareVolume.setFieldValue("tagPiPortionToFlare", []);
    }
  };

  const onChangeVolumeBy = (val: string) => {
    if (val === VolumeByEnum.FLOW_METER) {
      formSetupFlareVolume.setFieldValue("maxFlow", undefined);
      formSetupFlareVolume.setFieldValue("percentPortion", undefined);
      formSetupFlareVolume.setFieldValue("sentOneStackOfFlare", undefined);
      formSetupFlareVolume.setFieldValue("tagPiPortionToFlare", []);
    }
  };

  // console.log("TEST VALUE +:: ", formSetupFlareVolume.getFieldsValue());

  return (
    <Modal
      title={<ModalTitle title="Flare Volume" />}
      open={visibleModal}
      width={"95vw"}
      onCancel={closeModal}
      footer={
        <Row className="w-full border-t" justify={"end"}>
          <Button className="mt-4" key="submit" type="primary" loading={loadingManageFlareVolume} onClick={submitSetupHeatingValue}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
      destroyOnClose
    >
      {errorFlareVolume && (
        <Row className="w-full" justify={"center"}>
          <Text type="danger">{errorFlareVolume}</Text>
        </Row>
      )}
      <Form
        layout={"vertical"}
        name={"setup-heating-value"}
        autoComplete="off"
        form={formSetupFlareVolume}
        requiredMark={false}
        onFieldsChange={(_) => {
          if (errorFlareVolume) {
            clearError("errorFlareVolume");
            formSetupFlareVolume.setFields([
              {
                name: "sourceName",
                errors: undefined,
              },
              {
                name: "gasName",
                errors: undefined,
              },
              {
                name: "area",
                errors: undefined,
              },
              {
                name: "flareName",
                errors: undefined,
              },
            ]);
          }
        }}
      >
        <Row>
          <Col
            span={
              inputBy &&
              inputBy === InputByFixedEnum.PI &&
              volumeBy &&
              volumeBy === VolumeByEnum.OPEN_VALUE &&
              sentOneStackOfFlare &&
              sentOneStackOfFlare === SentOneStackOfFlareEnum.NO
                ? 15
                : 24
            }
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Source Name"} required />}
                  name="sourceName"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Source Name"),
                    },
                  ]}
                >
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Gas Name"} required />}
                  name="gasName"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Gas Name"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    options={optionsHeatingValues}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Area"} required />}
                  name="area"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Area"),
                    },
                  ]}
                >
                  <Select options={optionsAreaInCompany} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Flare Name"} required />}
                  name="flareName"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Flare Name"),
                    },
                  ]}
                >
                  <Select options={optionsFlareInformation} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Calculation Method"} required />}
                  name="calculationMethod"
                  initialValue={"Emission Factor"}
                >
                  <Select options={CalculationMethodFixed} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  label={<FormLabel label={"Input By"} required />}
                  name="inputBy"
                  rules={[
                    {
                      required: true,
                      message: generateErrorMessage("Input By"),
                    },
                  ]}
                >
                  <Select
                    options={InputByFixed}
                    onChange={(val) => {
                      onChangeFlareVolume(val);
                      clearErrorFlareVolume();
                    }}
                  />
                </Form.Item>
              </Col>
              {inputBy && inputBy === InputByFixedEnum.PI && (
                <>
                  <Col xs={24} sm={12} lg={12}>
                    <Form.Item
                      label={<FormLabel label={"%HC Volume"} required />}
                      name="hcVolume"
                      rules={[
                        {
                          required: true,
                          message: generateErrorMessage("HC Volume By"),
                        },
                      ]}
                    >
                      <CustomInputNumber
                        onChange={(e) => formSetupFlareVolume.setFieldValue("hcVolume", !isNil(e) ? parseFloat(`${e}`).toFixed(2) : null)}
                        precision={2}
                        addonAfter={"%"}
                        onKeyDown={(e) => {
                          onKeyDownNumberWithFixedDecimal(e, 2);
                        }}
                        max={100}
                      />
                    </Form.Item>
                  </Col>
                  {volumeBy === VolumeByEnum.OPEN_VALUE && (
                    <Col xs={24} sm={12} lg={12}>
                      <Form.Item label={<FormLabel label={"%Portion"} />} name="percentPortion">
                        <Input addonAfter={"%"} />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} sm={12} lg={12}>
                    <Form.Item
                      label={<FormLabel label={"Volume By"} required />}
                      name="volumeBy"
                      rules={[
                        {
                          required: true,
                          message: generateErrorMessage("Volume By"),
                        },
                      ]}
                    >
                      <Select
                        options={VolumeByOption}
                        onChange={(val) => {
                          clearErrorFlareVolume();
                          onChangeVolumeBy(val);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {volumeBy && volumeBy === VolumeByEnum.OPEN_VALUE && (
                    <>
                      <Col xs={24} sm={12} lg={12}>
                        <Form.Item
                          label={<FormLabel label={"Max Flow"} required />}
                          name="maxFlow"
                          rules={[
                            {
                              required: true,
                              message: generateErrorMessage("Max Flow"),
                            },
                          ]}
                        >
                          <CustomInputNumber
                            onChange={(e) => formSetupFlareVolume.setFieldValue("maxFlow", !isNil(e) ? `${e}` : null)}
                            step={1}
                            min={0}
                            addonAfter={"Kg/h"}
                            onKeyDown={(e) => {
                              onKeyDownNumberWithFixedDecimal(e, 2);
                            }}
                            formatter={(value) => formatNumber(value as number)}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  <Col xs={24} sm={12} lg={12}>
                    <Form.Item
                      label={<FormLabel label={"Tag PI"} required />}
                      name="tagPi"
                      rules={[
                        {
                          required: true,
                          message: generateErrorMessage("Tag PI"),
                        },
                      ]}
                    >
                      <Input addonAfter={volumeBy && volumeBy === VolumeByEnum.OPEN_VALUE ? "%" : "Kg"} />
                    </Form.Item>
                  </Col>

                  {volumeBy && volumeBy === VolumeByEnum.OPEN_VALUE && (
                    <>
                      <Col xs={24} sm={12} lg={12}>
                        <Form.Item
                          label={<FormLabel label={"Sent one stack of flare"} required />}
                          name="sentOneStackOfFlare"
                          rules={[
                            {
                              required: true,
                              message: generateErrorMessage("Sent one stack of flare"),
                            },
                          ]}
                        >
                          <Select options={SentOneStackOfFlareOption} onChange={onChangeSentOneStackOfFlare} />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
                    <CustomFormDatePicker
                      name={"turnaroundPeriod"}
                      label={"Turnaround Period"}
                      customClassName={"turnaround-period"}
                      disabledRanges={turnaroundPeriodRanges}
                    />
                  </Col>

                  <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
                    <CustomFormDatePicker
                      name={"emergencyPeriod"}
                      label={"Emergency Period"}
                      customClassName={"emergency-period"}
                      disabledRanges={emergencyPeriodRanges}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
                    <CustomFormDatePicker
                      name={"exceptionDate"}
                      label={"Exception Date"}
                      customClassName={"exception-date-period"}
                      disabledRanges={exceptionDateRanges}
                    />
                  </Col>
                </>
              )}
            </Row>
          </Col>
          {inputBy &&
            inputBy === InputByFixedEnum.PI &&
            volumeBy &&
            volumeBy === VolumeByEnum.OPEN_VALUE &&
            sentOneStackOfFlare &&
            sentOneStackOfFlare === SentOneStackOfFlareEnum.NO && (
              <Col span={9} className="pl-4">
                <Row gutter={[0, 0]}>
                  <Col span={24}>
                    <FormLabel label={"Tag PI Portion to Flare"} />
                  </Col>

                  <Form.List name="tagPiPortionToFlare">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, index) => (
                          <GroupFlareTagPi
                            key={key}
                            index={index}
                            name={name}
                            restField={restField}
                            optionsFlareInformation={optionsFlareInformation}
                            formSetupFlareVolume={formSetupFlareVolume}
                            remove={remove}
                            fields={fields}
                            errorFlareVolume={errorFlareVolume}
                          />
                        ))}
                        <Form.Item className="w-full">
                          <Row className="w-full mt-2" justify={"end"}>
                            <Button
                              id={"add-tag-pi-portion-group"}
                              type="primary"
                              size={"middle"}
                              className="flex items-center"
                              onClick={() => {
                                add();
                                clearErrorFlareVolume();
                              }}
                            >
                              <PlusCircleOutlined />
                            </Button>
                          </Row>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Row>
              </Col>
            )}
        </Row>
      </Form>
    </Modal>
  );
};

export default FlareVolumeModal;
